import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ContextOverview } from "../components/ContextOverview"
import { ContextMarkdownEditor } from "../components/ContextMarkdownEditor"
import { WorkspaceSearch } from "../components/WorkspaceSearch"
import { workspaceSearchItems } from "../lib/workspaceSearch"
import { conceptContextEvidenceSignature, contextOverviewFromPayload, contextTimestampFromPayload } from "../lib/repositoryContext"
import {
  BackButton,
  Notice,
  normalizeWorkspaceSection,
  readSidebarCollapsedState,
  shouldHandleClientNavigation,
  workspaceSectionHref,
  workspaceSectionPathsFor,
  WorkspaceSidebar,
  writeSidebarCollapsedState
} from "../components/Chrome"
import { Inbox, mergeInboxIssuePayloads } from "../components/Inbox"
import { ConceptRail, IssueRail } from "../components/IssueRail"
import { RepositoryConcepts, conceptAsyncActive, conceptCommitHistoryPath, conceptReadingPaused } from "../components/RepositoryConcepts"
import { csrfToken as readCsrfToken } from "../lib/rails"
import { scrollPageToTop } from "../lib/scroll"
import { RepositoryOnboardingProgress } from "../components/RepositoryOnboardingProgress"
import { TeamPanel } from "./TeamPage"

const prefetchedRepositoryImageAssets = new Set()
const prefetchedRepositoryDocumentAssets = new Set()
const defaultRepositorySettings = {
  defaultSyncCommitThreshold: 100,
  maxSyncCommitThreshold: 10000,
  minSyncCommitThreshold: 1,
  syncCommitThreshold: 100,
  updatePath: null
}

function initialIssueCategorizationStatus(issueCategorization) {
  return issueCategorization?.status || "idle"
}

export function assertIssueCategorizationSucceeded(payload) {
  if (payload?.status !== "failed") return

  throw new Error(payload.error_message || payload.error || "Issue categorization failed.")
}

function repositoryUpdateBusy(update) {
  return update?.status === "running"
}

export function mergeRepositoryArchitecturePayload(current, architecture) {
  return {
    ...current,
    available: architecture.available ?? current.available,
    status: architecture.status || current.status,
    paths: {
      ...current.paths,
      markdown: architecture.markdownUrl || current.paths.markdown
    }
  }
}

export function repositoryImageAssetUrls({ contextOverview, issues = [], storyboards = [] } = {}) {
  const urls = []

  toArray(contextOverview?.users).forEach((user) => {
    appendAssetUrl(urls, user?.avatar?.url)
  })

  toArray(issues).forEach((issue) => {
    appendAssetUrl(urls, issue?.visual?.url)
    if (issue?.reportStatus !== "ready") return

    appendAssetUrl(urls, issue.reportThumbnailPath || issue.reportInfographicPath)
  })

  toArray(storyboards).forEach((storyboard) => {
    const frames = [ ...toArray(storyboard?.frames), ...toArray(storyboard?.uiFrames) ]

    frames.forEach((frame) => {
      appendAssetUrl(urls, frame?.thumbnailUrl)
    })
  })

  return uniqueImageAssetUrls(urls)
}

export function repositoryDocumentAssetUrls({ contextDiagrams = null, issues = [] } = {}) {
  const urls = []

  toArray(issues).forEach((issue) => {
    appendDiagramDocumentUrls(urls, issue?.technicalDiagrams)
  })
  appendDiagramDocumentUrls(urls, contextDiagrams?.diagrams)

  return uniqueAssetUrls(urls)
}

export function prefetchRepositoryImageAssets(urls = [], documentRef = globalThis.document) {
  return prefetchRepositoryAssets(urls, documentRef, {
    as: "image",
    datasetValue: "repository-image-asset",
    prefetchedAssets: prefetchedRepositoryImageAssets
  })
}

export function prefetchRepositoryDocumentAssets(urls = [], documentRef = globalThis.document) {
  return prefetchRepositoryAssets(urls, documentRef, {
    as: "document",
    datasetValue: "repository-document-asset",
    prefetchedAssets: prefetchedRepositoryDocumentAssets
  })
}

function prefetchRepositoryAssets(urls = [], documentRef = globalThis.document, { as, datasetValue, prefetchedAssets }) {
  if (!documentRef?.head || typeof documentRef.createElement !== "function") return []

  return uniqueAssetUrls(urls).filter((url) => {
    if (prefetchedAssets.has(url) || documentHasAssetHint(documentRef, url, as)) {
      prefetchedAssets.add(url)
      return false
    }

    const link = documentRef.createElement("link")
    link.rel = "prefetch"
    link.as = as
    link.href = url
    link.dataset.vibecheckPrefetch = datasetValue
    documentRef.head.appendChild(link)
    prefetchedAssets.add(url)

    return true
  })
}

function appendDiagramDocumentUrls(urls, diagrams) {
  toArray(diagrams).forEach((diagram) => {
    if (diagram?.status !== "ready") return

    appendAssetUrl(urls, diagram.embedUrl || diagram.standaloneUrl)
  })
}

function appendAssetUrl(urls, url) {
  const normalizedUrl = typeof url === "string" ? url.trim() : ""
  if (normalizedUrl) urls.push(normalizedUrl)
}

function uniqueImageAssetUrls(urls) {
  return uniqueAssetUrls(urls)
}

function uniqueAssetUrls(urls) {
  return Array.from(new Set(toArray(urls).map((url) => (
    typeof url === "string" ? url.trim() : ""
  )).filter(Boolean)))
}

function documentHasAssetHint(documentRef, url, as) {
  if (typeof documentRef.querySelectorAll !== "function") return false

  return Array.from(documentRef.querySelectorAll("link[rel][href]")).some((link) => {
    const rel = String(link.rel || link.getAttribute?.("rel") || "").toLowerCase()
    const linkAs = String(link.as || link.getAttribute?.("as") || "").toLowerCase()
    const href = link.getAttribute?.("href") || link.href

    return (rel.split(/\s+/).includes("preload") || rel.split(/\s+/).includes("prefetch")) &&
      linkAs === as &&
      href === url
  })
}

function toArray(value) {
  return Array.isArray(value) ? value : []
}

function normalizeRepositorySettings(settings = {}) {
  const defaults = {
    ...defaultRepositorySettings,
    ...settings
  }
  const min = normalizedInteger(defaults.minSyncCommitThreshold, defaultRepositorySettings.minSyncCommitThreshold)
  const max = normalizedInteger(defaults.maxSyncCommitThreshold, defaultRepositorySettings.maxSyncCommitThreshold)
  const defaultThreshold = clampNumber(
    normalizedInteger(defaults.defaultSyncCommitThreshold, defaultRepositorySettings.defaultSyncCommitThreshold),
    min,
    max
  )
  const threshold = clampNumber(
    normalizedInteger(defaults.syncCommitThreshold, defaultThreshold),
    min,
    max
  )

  return {
    ...defaults,
    defaultSyncCommitThreshold: defaultThreshold,
    maxSyncCommitThreshold: max,
    minSyncCommitThreshold: min,
    syncCommitThreshold: threshold,
    updatePath: defaults.updatePath || null
  }
}

function normalizedThresholdFromDraft(value, settings = {}) {
  const trimmed = value.toString().trim()
  if (!/^\d+$/.test(trimmed)) return null

  const normalizedSettings = normalizeRepositorySettings(settings)
  return clampNumber(
    Number(trimmed),
    normalizedSettings.minSyncCommitThreshold,
    normalizedSettings.maxSyncCommitThreshold
  )
}

function normalizedInteger(value, fallback) {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Math.trunc(numeric) : fallback
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

const SYNC_NOW_TASKS = [
  { key: "fetching_code", label: "Fetching latest code" },
  { key: "regenerating_map", label: "Regenerating the repository map" },
  { key: "processing_commits", label: "Processing new commits" },
  { key: "syncing_context", label: "Syncing context" },
  { key: "categorizing_issues", label: "Assigning issue categories" },
  { key: "syncing_concepts", label: "Syncing concepts" }
]

// Task keys driven by the server-reported repository update phase. They mirror
// WorkspaceRepository::REPOSITORY_UPDATE_PHASES.
const SYNC_UPDATE_PHASE_TASK_KEYS = [ "fetching_code", "regenerating_map", "processing_commits" ]
// Backstop for the repository update wait: the server marks a stranded update
// failed after 30 minutes, so the client gives up shortly after that.
const REPOSITORY_UPDATE_WAIT_TIMEOUT_MS = 35 * 60 * 1000

function initialSyncNowTasks() {
  return SYNC_NOW_TASKS.map((task) => ({ ...task, status: "queued", detail: null }))
}

export function RepositoryClonePage(props) {
  if (repositoryOnboardingVisible(props.onboarding, props.repository?.onboardingGateBypassed)) {
    return (
      <RepositoryOnboardingProgress
        conceptCount={props.concept?.concepts?.items?.length || 0}
        initialConceptItems={props.concept?.concepts?.items || null}
        onboarding={props.onboarding}
        repository={props.repository}
        workspacePaths={props.paths?.workspaceSections}
      />
    )
  }

  return <RepositoryWorkspacePage {...props} />
}

export function repositoryOnboardingVisible(onboarding, onboardingGateBypassed = false) {
  return !onboardingGateBypassed && Boolean(onboarding) && (
    onboarding.active === true ||
    onboarding.status !== "completed"
  )
}

function RepositoryWorkspacePage({
  agentGeneration = null,
  alert,
  architecture,
  concept = null,
  contextRefresh = null,
  contextActions = null,
  contextDiagrams = null,
  contextPersonas = null,
  contextStoryboards = null,
  contextOverview = null,
  csrfToken,
  githubLogin,
  inbox = null,
  issueCategorization = null,
  issues = [],
  paths,
  repository,
  settings = null,
  team = null
}) {
  const readOnly = repository.readOnly === true
  const [architectureState, setArchitectureState] = useState(architecture)
  const architectureStreamLines = []
  const [contextRefreshing, setContextRefreshing] = useState(false)
  const [contextRefreshStreamLines, setContextRefreshStreamLines] = useState([])
  const [contextLastUpdatedAt, setContextLastUpdatedAt] = useState(() => contextTimestampFromPayload(contextRefresh))
  const [repositoryContextOverview, setRepositoryContextOverview] = useState(contextOverview)
  const [selectedContextActionKey, setSelectedContextActionKey] = useState(() => readInitialContextActionKey(contextActions))
  const [selectedContextPersonaKey, setSelectedContextPersonaKey] = useState(() => readInitialContextPersonaKey(contextPersonas))
  const [hoveredIssueKey, setHoveredIssueKey] = useState(null)
  const [inboxIssues, setInboxIssues] = useState(issues)
  const [repositoryUpdate, setRepositoryUpdate] = useState(inbox?.update || null)
  const [repositorySettings, setRepositorySettings] = useState(() => normalizeRepositorySettings(settings))
  const [syncNowRunning, setSyncNowRunning] = useState(false)
  const [syncNowStatus, setSyncNowStatus] = useState(null)
  const [syncNowTasks, setSyncNowTasks] = useState(() => initialSyncNowTasks())
  const [syncNowEventId, setSyncNowEventId] = useState(null)
  const syncNowUpdatePhaseTaskKeyRef = useRef(null)
  const [syncProgressVisible, setSyncProgressVisible] = useState(false)
  const [syncSettingsSaving, setSyncSettingsSaving] = useState(false)
  const [repositoryConcept, setRepositoryConcept] = useState(concept)
  const [issueCategorizationStatus, setIssueCategorizationStatus] = useState(initialIssueCategorizationStatus(issueCategorization))
  const [activeWorkspaceSection, setActiveWorkspaceSection] = useState(() => (
    readInitialWorkspaceSection(repository.activeSection, { githubLogin, repository })
  ))
  const [inboxRoute, setInboxRoute] = useState(() => readInitialInboxRoute(inbox))
  const [inboxStatusToast, setInboxStatusToast] = useState(null)
  const [mobileWorkspaceNavOpen, setMobileWorkspaceNavOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsedState)
  // While a replay session is open the concept atlas hands up a closer; the
  // sidebar's logo and settings buttons become exits that call it.
  const [replayExit, setReplayExit] = useState(null)
  const [replayRequested, setReplayRequested] = useState(() => navigationStateFromLocation(windowPathname()).replayRequested)
  const [replayAutoplay, setReplayAutoplay] = useState(replayRequested)
  const handleReplaySessionChange = useCallback((close) => setReplayExit(() => close), [])
  const autoIssueCategorizationStartedRef = useRef(false)
  const issueCategorizationPollRef = useRef(null)
  const inboxStatusToastTimeoutRef = useRef(null)
  // Pathname captured when the issue panel opens, so closing it can restore
  // deep section URLs (e.g. a persona detail) instead of the section root.
  const inboxPanelReturnPathRef = useRef(null)
  // Last pathname applied by applyLocationState, so it can detect transitions
  // into an issue detail and stash the return path above.
  const lastNavigationPathnameRef = useRef(typeof window === "undefined" ? null : window.location?.pathname || null)
  const repositoryUpdatePollRef = useRef(null)
  const repositoryConceptRef = useRef(concept)
  const repositoryConceptSwapActiveRef = useRef(false)
  const repositoryConceptSwapPublishEnabledRef = useRef(false)
  const repositoryConceptSwapBufferRef = useRef(null)
  const contextConceptEvidenceSignatureRef = useRef(conceptContextEvidenceSignature(contextOverview))
  const conceptRefreshTimeoutRef = useRef(null)
  const conceptRefreshInFlightRef = useRef(false)
  const conceptRefreshPendingRef = useRef(false)
  const conceptRefreshRepositorySyncEventIdRef = useRef(null)
  const resumeIssueCategorizationPollingRef = useRef(issueCategorization?.status === "running")
  const resumeRepositoryUpdatePollingRef = useRef(inbox?.update?.status === "running")
  useEffect(() => {
    prefetchRepositoryImageAssets(repositoryImageAssetUrls({
      contextOverview: repositoryContextOverview,
      issues: inboxIssues,
      storyboards: contextStoryboards?.storyboards
    }))
    prefetchRepositoryDocumentAssets(repositoryDocumentAssetUrls({
      contextDiagrams,
      issues: inboxIssues
    }))
  }, [contextDiagrams, contextStoryboards?.storyboards, inboxIssues, repository.fullName, repositoryContextOverview])

  useEffect(() => {
    writeSidebarCollapsedState(sidebarCollapsed)
  }, [sidebarCollapsed])

  // The issue panel floats over the current section with its own scroll, so
  // inboxRoute changes must not yank the page underneath it back to the top.
  useEffect(() => {
    scrollPageToTop()
  }, [activeWorkspaceSection, repositoryConcept?.selectedAtlas, repositoryConcept?.selectedConceptKey, selectedContextActionKey, selectedContextPersonaKey])

  useEffect(() => {
    repositoryConceptRef.current = repositoryConcept
  }, [repositoryConcept])

  useEffect(() => () => {
    stopIssueCategorizationPolling()
    stopRepositoryUpdatePolling()
    if (conceptRefreshTimeoutRef.current) window.clearTimeout(conceptRefreshTimeoutRef.current)
    if (inboxStatusToastTimeoutRef.current) window.clearTimeout(inboxStatusToastTimeoutRef.current)
  }, [])

  useEffect(() => {
    if (!resumeIssueCategorizationPollingRef.current) return undefined

    resumeIssueCategorizationPollingRef.current = false
    startIssueCategorizationPolling({ pollNow: true })

    return () => stopIssueCategorizationPolling()
  }, [])

  useEffect(() => {
    if (!resumeRepositoryUpdatePollingRef.current) return undefined

    resumeRepositoryUpdatePollingRef.current = false
    startRepositoryUpdatePolling({ pollNow: true })

    return () => stopRepositoryUpdatePolling()
  }, [])

  // The URL is canonical: every navigation (menu click, panel close,
  // back/forward) pushStates and then funnels through applyLocationState,
  // which derives every panel and selection from the pathname in one
  // consistent step.
  function applyLocationState() {
    if (typeof window === "undefined") return

    const pathname = window.location.pathname
    const hashSection = normalizeWorkspaceSection(window.location?.hash?.toString(), null)
    const next = navigationStateFromLocation(pathname, { currentSection: activeWorkspaceSection, repository, replayActive: replayRequested })
    const section = hashSection || next.section

    // Remember where the issue panel was opened from — including opens via
    // back/forward — so closing it restores the underlying page.
    const previousPathname = lastNavigationPathnameRef.current
    if (next.inboxRoute.view === "detail" && previousPathname && previousPathname !== pathname) {
      const previous = navigationStateFromLocation(previousPathname, { currentSection: section, repository })
      if (previous.inboxRoute.view !== "detail") inboxPanelReturnPathRef.current = previousPathname
    }
    lastNavigationPathnameRef.current = pathname

    setActiveWorkspaceSection(section)
    setReplayRequested(next.replayRequested)
    setMobileWorkspaceNavOpen(false)
    setInboxRoute(next.inboxRoute)
    setSelectedContextActionKey(next.contextActionKey)
    setSelectedContextPersonaKey(next.contextPersonaKey)
    setRepositoryConcept((current) => (
      current
        ? {
            ...current,
            selectedConceptKey: next.selectedConceptKey,
            selectedConceptView: next.selectedConceptView,
            selectedAtlas: next.selectedAtlas
          }
        : current
    ))
    bufferRepositoryConceptSelection(next.selectedConceptKey, next.selectedAtlas, next.selectedConceptView)
  }

  const applyLocationStateRef = useRef(null)
  applyLocationStateRef.current = applyLocationState

  useEffect(() => {
    function handlePopState() {
      applyLocationStateRef.current?.()
    }

    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  const architectureReady = architectureState.available
  const inboxPanelOpen = inboxRoute.view === "detail"
  const conceptDetailView = activeWorkspaceSection === "concept" &&
    Boolean(repositoryConcept?.selectedConceptKey || repositoryConcept?.selectedAtlas)
  const atlasSectionActive = activeWorkspaceSection === "concept" && !mobileWorkspaceNavOpen
  const issueCategorizationTriggerPath = issueCategorization?.paths?.trigger
  const contextGenerating = !architectureReady && !repositoryContextOverview
  const contextBusy = contextGenerating || contextRefreshing
  const repositorySwitcherRepositories = team?.repositories || []
  const workspaceSectionPaths = useMemo(() => ({
    ...workspaceSectionPathsFor(repository, paths.root),
    ...(paths.workspaceSections || {})
  }), [paths.root, paths.workspaceSections, repository])
  const contextActionListPath = contextActions?.listPath || workspaceSectionPaths.domains
  const contextPersonaListPath = contextPersonas?.listPath || contextActionListPath
  const conceptListPath = repositoryConcept?.listPath || workspaceSectionPaths.concept
  const conceptDetailBasePath = repositoryConcept?.detailBasePath || conceptListPath
  const contextDetailOpen = activeWorkspaceSection === "domains" && Boolean(selectedContextActionKey || selectedContextPersonaKey)
  useEffect(() => {
    if (!architectureReady || autoIssueCategorizationStartedRef.current) return
    if (!shouldStartStackIssueCategorization(inboxIssues, issueCategorizationTriggerPath)) return

    autoIssueCategorizationStartedRef.current = true
    setIssueCategorizationStatus("pending")
    startIssueCategorization()
  }, [architectureReady, inboxIssues, issueCategorizationTriggerPath])

  const searchableItems = useMemo(() => workspaceSearchItems({
    concepts: repositoryConcept?.concepts?.items || [],
    personas: repositoryContextOverview?.users || [],
    actions: repositoryContextOverview?.actions || [],
    issues: inboxIssues,
    conceptPath: conceptListPath,
    contextPath: workspaceSectionPaths.domains
  }), [repositoryConcept?.concepts?.items, repositoryContextOverview, inboxIssues, conceptListPath, workspaceSectionPaths.domains])

  function openSearch() {
    replayExit?.()
    setSearchOpen(true)
  }

  function selectSearchResult(result, event) {
    if (event && !shouldHandleClientNavigation(event)) return
    event?.preventDefault()
    setSearchOpen(false)
    if (result.type === "issue") navigateToInboxIssue(result.item)
    else if (result.type === "concept") navigateToConcept(result.item)
    else if (result.type === "context") handleWorkspaceNavigate("domains")
    else navigateToAtlasDetail(result.type, result.item.key)
  }

  useEffect(() => {
    function handleSearchShortcut(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        openSearch()
      }
    }
    window.addEventListener("keydown", handleSearchShortcut)
    return () => window.removeEventListener("keydown", handleSearchShortcut)
  }, [replayExit])

  function handleContextRefreshComplete(payload) {
    const nextContext = contextOverviewFromPayload(payload)
    if (nextContext) handleRepositoryContextUpdate(nextContext)
    setContextLastUpdatedAt(contextTimestampFromPayload(payload, new Date().toISOString()))
  }

  function handleRepositoryContextUpdate(nextContext, { refreshConcepts = true } = {}) {
    if (!nextContext) return

    setRepositoryContextOverview(nextContext)
    const nextSignature = conceptContextEvidenceSignature(nextContext)
    const conceptEvidenceChanged = nextSignature !== contextConceptEvidenceSignatureRef.current
    contextConceptEvidenceSignatureRef.current = nextSignature

    if (refreshConcepts && conceptEvidenceChanged) scheduleConceptRefreshAfterContextUpdate()
  }

  function scheduleConceptRefreshAfterContextUpdate() {
    if (typeof window === "undefined") return
    if (!repositoryConceptRef.current?.path) return

    if (conceptRefreshTimeoutRef.current) {
      window.clearTimeout(conceptRefreshTimeoutRef.current)
    }

    conceptRefreshTimeoutRef.current = window.setTimeout(() => {
      conceptRefreshTimeoutRef.current = null
      refreshConceptsAfterContextUpdate()
    }, 350)
  }

  function refreshConceptsAfterContextUpdate() {
    const conceptState = repositoryConceptRef.current
    const conceptPath = conceptState?.path
    if (!conceptPath) return

    if (conceptRefreshInFlightRef.current) {
      conceptRefreshPendingRef.current = true
      return
    }

    conceptRefreshInFlightRef.current = true
    let requestPath = conceptState.selectedConceptKey
      ? appendQueryParam(conceptPath, "concept_key", conceptState.selectedConceptKey)
      : conceptPath
    if (conceptRefreshRepositorySyncEventIdRef.current) {
      requestPath = appendQueryParam(requestPath, "repository_sync_event_id", conceptRefreshRepositorySyncEventIdRef.current)
    }

    fetch(requestPath, {
      cache: "no-store",
      headers: noStoreJsonHeaders()
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not refresh concepts.")
        return response.json()
      })
      .then((payload) => {
        handleRepositoryConceptPayload(repositoryConceptPayloadFromConceptsResponse(payload, conceptState, conceptPath))
      })
      .catch(() => {})
      .finally(() => {
        conceptRefreshInFlightRef.current = false
        if (!conceptRefreshPendingRef.current) return

        conceptRefreshPendingRef.current = false
        scheduleConceptRefreshAfterContextUpdate()
      })
  }

  function beginRepositoryConceptSwapBuffer() {
    repositoryConceptSwapActiveRef.current = true
    repositoryConceptSwapPublishEnabledRef.current = false
    repositoryConceptSwapBufferRef.current = repositoryConceptRef.current || null
  }

  function discardRepositoryConceptSwapBuffer() {
    repositoryConceptSwapActiveRef.current = false
    repositoryConceptSwapPublishEnabledRef.current = false
    repositoryConceptSwapBufferRef.current = null
    conceptRefreshRepositorySyncEventIdRef.current = null
  }

  function enableRepositoryConceptSwapPublishing() {
    repositoryConceptSwapPublishEnabledRef.current = true
    return publishRepositoryConceptSwapIfReady()
  }

  function publishRepositoryConceptSwapIfReady() {
    const bufferedConcept = repositoryConceptSwapBufferRef.current
    if (!repositoryConceptSwapActiveRef.current) return false
    if (!repositoryConceptSwapPublishEnabledRef.current) return false
    if (!repositoryConceptReadyForAtomicSwap(bufferedConcept)) return false

    repositoryConceptSwapActiveRef.current = false
    repositoryConceptSwapPublishEnabledRef.current = false
    repositoryConceptSwapBufferRef.current = null
    conceptRefreshRepositorySyncEventIdRef.current = null
    setRepositoryConcept((current) => mergeRepositoryConceptPayload(current, bufferedConcept))
    return true
  }

  function handleRepositoryConceptPayload(nextConcept) {
    if (!nextConcept) return false

    if (repositoryConceptSwapActiveRef.current) {
      const currentBufferedConcept = repositoryConceptSwapBufferRef.current || repositoryConceptRef.current || null
      repositoryConceptSwapBufferRef.current = mergeRepositoryConceptPayload(currentBufferedConcept, nextConcept)
      return publishRepositoryConceptSwapIfReady()
    }

    setRepositoryConcept((current) => mergeRepositoryConceptPayload(current, nextConcept))
    return repositoryConceptReadyForAtomicSwap(nextConcept)
  }

  function bufferRepositoryConceptSummary(summary) {
    const currentBufferedConcept = repositoryConceptSwapBufferRef.current || repositoryConceptRef.current || {}
    repositoryConceptSwapBufferRef.current = currentBufferedConcept ? {
      ...currentBufferedConcept,
      concepts: currentBufferedConcept.concepts ? { ...currentBufferedConcept.concepts, summary } : currentBufferedConcept.concepts,
      summary
    } : { summary }
    return publishRepositoryConceptSwapIfReady()
  }

  function bufferRepositoryConceptSelection(selectedConceptKey, selectedAtlas = null, selectedConceptView = null) {
    if (!repositoryConceptSwapActiveRef.current) return

    const currentBufferedConcept = repositoryConceptSwapBufferRef.current || repositoryConceptRef.current || {}
    repositoryConceptSwapBufferRef.current = {
      ...currentBufferedConcept,
      selectedAtlas: selectedAtlas || null,
      selectedConceptKey: selectedConceptKey || null,
      selectedConceptView: selectedConceptView || null
    }
  }

  function repositoryConceptFetchPath(repositorySyncEventId = null) {
    const conceptState = repositoryConceptSwapBufferRef.current || repositoryConceptRef.current
    const conceptPath = conceptState?.path
    if (!conceptPath) return null

    let requestPath = conceptState.selectedConceptKey
      ? appendQueryParam(conceptPath, "concept_key", conceptState.selectedConceptKey)
      : conceptPath
    if (repositorySyncEventId) requestPath = appendQueryParam(requestPath, "repository_sync_event_id", repositorySyncEventId)

    return requestPath
  }

  function fetchRepositoryConceptPayload(repositorySyncEventId = null) {
    const requestPath = repositoryConceptFetchPath(repositorySyncEventId)
    if (!requestPath) return Promise.resolve(null)

    return fetch(requestPath, {
      cache: "no-store",
      headers: noStoreJsonHeaders()
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not refresh concepts.")
        return response.json()
      })
      .then((payload) => (
        repositoryConceptPayloadFromConceptsResponse(
          payload,
          repositoryConceptSwapBufferRef.current || repositoryConceptRef.current,
          (repositoryConceptSwapBufferRef.current || repositoryConceptRef.current)?.path
        )
      ))
  }

  function waitForRepositoryConceptSwapCompletion({ repositorySyncEventId = null } = {}) {
    enableRepositoryConceptSwapPublishing()
    if (!repositoryConceptFetchPath(repositorySyncEventId)) {
      publishRepositoryConceptSwapIfReady()
      return Promise.resolve(null)
    }

    return new Promise((resolve, reject) => {
      function poll() {
        fetchRepositoryConceptPayload(repositorySyncEventId)
          .then((nextConcept) => {
            if (handleRepositoryConceptPayload(nextConcept)) {
              resolve(nextConcept)
              return
            }

            window.setTimeout(poll, 4000)
          })
          .catch(reject)
      }

      poll()
    })
  }

  function handleIssueUpdate(updatedIssue) {
    setInboxIssues((current) => current.map((issue) => (
      issue.id === updatedIssue.id ? updatedIssue : issue
    )))
  }

  function showInboxStatusToast(message) {
    if (!message) return

    if (inboxStatusToastTimeoutRef.current) {
      window.clearTimeout(inboxStatusToastTimeoutRef.current)
    }

    setInboxStatusToast({ id: Date.now(), message })

    if (typeof window !== "undefined") {
      inboxStatusToastTimeoutRef.current = window.setTimeout(() => {
        setInboxStatusToast(null)
        inboxStatusToastTimeoutRef.current = null
      }, 3200)
    }
  }

  function openReplay(event, { autoplay = true } = {}) {
    if (event && !shouldHandleClientNavigation(event)) return

    event?.preventDefault()
    setReplayAutoplay(autoplay)
    window.history.pushState({}, "", paths.repositoryPlay)
    applyLocationState()
  }

  function closeReplayRoute() {
    setReplayRequested(false)
    setReplayAutoplay(false)
    if (navigationStateFromLocation(windowPathname()).replayRequested) navigateToConceptList()
  }

  function closeConceptDetail(event) {
    if (replayRequested) openReplay(event, { autoplay: false })
    else navigateToConceptList(event)
  }

  async function shareReplay() {
    try {
      await navigator.clipboard.writeText(new URL(paths.repositoryPlay, window.location.origin).href)
      showInboxStatusToast("URL copied")
    } catch {
      showInboxStatusToast("Couldn't copy URL. Please try again.")
    }
  }

  function stopRepositoryUpdatePolling() {
    if (!repositoryUpdatePollRef.current) return

    window.clearInterval(repositoryUpdatePollRef.current)
    repositoryUpdatePollRef.current = null
  }

  function startRepositoryUpdatePolling({ pollNow = false } = {}) {
    if (!repositoryUpdate?.statusPath) {
      setRepositoryUpdate((current) => ({
        ...(current || {}),
        status: "failed",
        error: "Could not check the repository update."
      }))
      return
    }

    if (pollNow) pollRepositoryUpdateStatus()
    if (repositoryUpdatePollRef.current) return

    repositoryUpdatePollRef.current = window.setInterval(pollRepositoryUpdateStatus, 3000)
  }

  function applyRepositoryUpdatePayload(payload) {
    const nextUpdate = payload.update || {}

    if (Object.keys(nextUpdate).length > 0) {
      setRepositoryUpdate((current) => ({ ...(current || {}), ...nextUpdate }))
    }

    if (payload.architecture) {
      setArchitectureState((current) => mergeRepositoryArchitecturePayload(current, payload.architecture))
    }

    if (payload.contextOverview || payload.context_overview) {
      handleRepositoryContextUpdate(payload.contextOverview || payload.context_overview, { refreshConcepts: !payload.concept })
    }
    if (Array.isArray(payload.issues)) {
      setInboxIssues((current) => mergeInboxIssuePayloads(current, payload.issues))
    }
    if (payload.concept) {
      handleRepositoryConceptPayload(payload.concept)
    }

    trackSyncNowUpdatePhase(nextUpdate)

    if (nextUpdate.status === "running") return false

    stopRepositoryUpdatePolling()
    return true
  }

  // Mirrors the server-reported repository update phase onto the sync-now task
  // list so the progress view shows which sub-step is running (and the latest
  // repository map activity while it regenerates).
  function trackSyncNowUpdatePhase(update) {
    const phaseIndex = SYNC_UPDATE_PHASE_TASK_KEYS.indexOf(update?.phase)
    if (phaseIndex === -1) return

    syncNowUpdatePhaseTaskKeyRef.current = update.phase
    if (update.status !== "running") return

    setSyncNowTasks((current) => current.map((task) => {
      const taskIndex = SYNC_UPDATE_PHASE_TASK_KEYS.indexOf(task.key)
      if (taskIndex === -1 || task.status === "failed" || task.status === "skipped") return task
      if (taskIndex < phaseIndex) return task.status === "completed" ? task : { ...task, status: "completed" }
      if (taskIndex === phaseIndex) return { ...task, status: "running", detail: update.phaseDetail || task.detail }
      return task
    }))
  }

  function handleConceptSummaryChange(summary) {
    if (repositoryConceptSwapActiveRef.current) {
      bufferRepositoryConceptSummary(summary)
      return
    }

    setRepositoryConcept((current) => current ? {
      ...current,
      concepts: current.concepts ? { ...current.concepts, summary } : current.concepts,
      summary
    } : { summary })
  }

  function handleConceptChange(nextConcept) {
    handleRepositoryConceptPayload(nextConcept)
  }

  function pollRepositoryUpdateStatus() {
    if (!repositoryUpdate?.statusPath) {
      setRepositoryUpdate((current) => ({
        ...(current || {}),
        status: "failed",
        error: "Could not check the repository update."
      }))
      return
    }

    fetch(repositoryUpdate.statusPath, {
      cache: "no-store",
      headers: noStoreJsonHeaders()
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not check the repository update.")
        return response.json()
      })
      .then((payload) => {
        if (!applyRepositoryUpdatePayload(payload)) {
          startRepositoryUpdatePolling()
        }
      })
      .catch((error) => {
        stopRepositoryUpdatePolling()
        setRepositoryUpdate((current) => ({
          ...(current || {}),
          status: "failed",
          error: error.message
        }))
      })
  }

  function repositoryUpdateStatusPath(statusPath, repositorySyncEventId) {
    if (!repositorySyncEventId || !statusPath) return statusPath

    const url = new URL(statusPath, window.location.origin)
    url.searchParams.set("repository_sync_event_id", repositorySyncEventId)
    return `${url.pathname}${url.search}${url.hash}`
  }

  function fetchRepositoryUpdateStatusPayload(statusPath = repositoryUpdate?.statusPath, repositorySyncEventId = null) {
    if (!statusPath) return Promise.reject(new Error("Could not check the repository update."))

    return fetch(repositoryUpdateStatusPath(statusPath, repositorySyncEventId), {
      cache: "no-store",
      headers: noStoreJsonHeaders()
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not check the repository update.")
        return response.json()
      })
  }

  function assertRepositoryUpdateSucceeded(payload) {
    if (payload?.update?.status !== "failed") return

    throw new Error(payload.update.error || "Repository sync failed.")
  }

  function waitForRepositoryUpdateCompletion(statusPath = repositoryUpdate?.statusPath, repositorySyncEventId = null) {
    return new Promise((resolve, reject) => {
      const deadline = Date.now() + REPOSITORY_UPDATE_WAIT_TIMEOUT_MS

      function poll() {
        fetchRepositoryUpdateStatusPayload(statusPath, repositorySyncEventId)
          .then((payload) => {
            if (!applyRepositoryUpdatePayload(payload)) {
              if (Date.now() >= deadline) {
                reject(new Error("The sync did not finish in time. It may have been interrupted — try Sync now again."))
                return
              }

              window.setTimeout(poll, 3000)
              return
            }

            assertRepositoryUpdateSucceeded(payload)
            resolve(payload)
          })
          .catch(reject)
      }

      poll()
    })
  }

  function syncRequestBody(repositorySyncEventId) {
    if (!repositorySyncEventId) return null

    return JSON.stringify({ repository_sync_event_id: repositorySyncEventId })
  }

  function noStoreJsonHeaders() {
    return {
      Accept: "application/json",
      "Cache-Control": "no-store"
    }
  }

  function csrfNoStoreJsonHeaders() {
    return {
      ...noStoreJsonHeaders(),
      "X-CSRF-Token": readCsrfToken() || csrfToken || ""
    }
  }

  function syncJsonHeaders() {
    return {
      ...csrfNoStoreJsonHeaders(),
      "Content-Type": "application/json"
    }
  }

  function syncMutationHeaders(repositorySyncEventId) {
    return repositorySyncEventId ? syncJsonHeaders() : csrfNoStoreJsonHeaders()
  }

  function createRepositorySyncEvent() {
    const createPath = paths.repositorySyncEvents?.create
    if (!createPath) return Promise.resolve(null)

    const metadata = { source: "settings-popover" }
    const conceptSnapshot = repositoryConceptSnapshotForSyncEvent(repositoryConceptRef.current)
    if (conceptSnapshot) metadata.concept_snapshot = conceptSnapshot

    return fetch(createPath, {
      body: JSON.stringify({
        repository_sync_event: {
          trigger: "context_sync_now",
          metadata
        }
      }),
      cache: "no-store",
      method: "POST",
      headers: syncJsonHeaders()
    }).then((response) => parseRepositorySyncEventResponse(response, "Could not start sync audit event."))
  }

  function parseRepositorySyncEventResponse(response, fallbackError) {
    return response.json()
      .catch(() => ({}))
      .then((payload) => {
        if (!response.ok) throw new Error(payload.error || fallbackError)
        return payload.repositorySyncEvent || payload.repository_sync_event || null
      })
  }

  function updateRepositorySyncEvent(syncEvent, status, errorMessage = null) {
    const createPath = paths.repositorySyncEvents?.create
    const eventId = syncEvent?.id
    if (!createPath || !eventId) return Promise.resolve(null)

    return fetch(`${createPath}/${eventId}`, {
      body: JSON.stringify({
        repository_sync_event: {
          status,
          error_message: errorMessage
        }
      }),
      cache: "no-store",
      method: "PATCH",
      headers: syncJsonHeaders()
    }).then((response) => parseRepositorySyncEventResponse(response, `Could not mark sync audit event ${status}.`))
  }

  function repositorySyncEventStatusPath(syncEventId) {
    const createPath = paths.repositorySyncEvents?.create
    if (!createPath || !syncEventId) return null

    return `${createPath}/${syncEventId}/status`
  }

  function startRepositoryUpdateNow({ repositorySyncEventId = null } = {}) {
    if (!repositoryUpdate?.path && !repositoryUpdate?.statusPath) {
      return Promise.resolve(null)
    }

    if (repositoryUpdateBusy(repositoryUpdate)) {
      return repositoryUpdate.statusPath ? waitForRepositoryUpdateCompletion(repositoryUpdate.statusPath, repositorySyncEventId) : Promise.resolve(null)
    }

    if (!repositoryUpdate?.path) return Promise.resolve(null)

    stopRepositoryUpdatePolling()
    setRepositoryUpdate((current) => ({
      ...(current || {}),
      status: "running",
      startedAt: new Date().toISOString(),
      startedAtInWords: "less than a minute ago",
      error: null
    }))

    return fetch(repositoryUpdate.path, {
      body: syncRequestBody(repositorySyncEventId),
      cache: "no-store",
      method: "POST",
      headers: syncMutationHeaders(repositorySyncEventId)
    })
      .then((response) => {
        if (!response.ok) {
          return response.json()
            .catch(() => ({}))
            .then((payload) => {
              throw new Error(payload.error || payload.update?.error || "Could not update from the repository.")
            })
        }

        return response.json()
      })
      .then((payload) => {
        if (!applyRepositoryUpdatePayload(payload)) {
          return waitForRepositoryUpdateCompletion(
            payload.update?.statusPath || repositoryUpdate.statusPath,
            repositorySyncEventId
          )
        }

        assertRepositoryUpdateSucceeded(payload)
        return payload
      })
  }

  function updateRepositoryFromRepo() {
    if (!repositoryUpdate?.path || repositoryUpdateBusy(repositoryUpdate)) return

    stopRepositoryUpdatePolling()
    setRepositoryUpdate((current) => ({
      ...(current || {}),
      status: "running",
      startedAt: new Date().toISOString(),
      startedAtInWords: "less than a minute ago",
      error: null
    }))

    fetch(repositoryUpdate.path, {
      cache: "no-store",
      method: "POST",
      headers: csrfNoStoreJsonHeaders()
    })
      .then((response) => {
        if (!response.ok) {
          return response.json()
            .catch(() => ({}))
            .then((payload) => {
              throw new Error(payload.error || payload.update?.error || "Could not update from the repository.")
            })
        }

        return response.json()
      })
      .then((payload) => {
        if (!applyRepositoryUpdatePayload(payload)) {
          startRepositoryUpdatePolling()
        }
      })
      .catch((error) => {
        stopRepositoryUpdatePolling()
        setRepositoryUpdate((current) => ({
          ...(current || {}),
          status: "failed",
          error: error.message || "Could not update from the repository."
        }))
      })
  }

  function saveSyncCommitThreshold(value) {
    const nextThreshold = normalizedThresholdFromDraft(String(value), repositorySettings)
    if (!nextThreshold) {
      return Promise.resolve(null)
    }

    if (!repositorySettings?.updatePath) {
      return Promise.resolve(null)
    }

    setSyncSettingsSaving(true)

    return fetch(repositorySettings.updatePath, {
      body: JSON.stringify({ settings: { sync_commit_threshold: nextThreshold } }),
      cache: "no-store",
      method: "PATCH",
      headers: syncJsonHeaders()
    })
      .then((response) => (
        response.json()
          .catch(() => ({}))
          .then((payload) => {
            if (!response.ok) throw new Error(payload.error || "Could not save sync settings.")
            return payload
          })
      ))
      .then((payload) => {
        const nextSettings = normalizeRepositorySettings({
          ...repositorySettings,
          ...(payload.settings || {}),
          syncCommitThreshold: payload.settings?.syncCommitThreshold ?? nextThreshold
        })
        setRepositorySettings(nextSettings)
        return nextSettings
      })
      .catch((error) => {
        console.error(error)
        return null
      })
      .finally(() => {
        setSyncSettingsSaving(false)
      })
  }

  function contextRefreshFinished(payload) {
    return payload?.status === "completed" || payload?.available
  }

  function assertContextRefreshSucceeded(payload) {
    if (payload?.status !== "failed") return

    throw new Error(payload.error_message || payload.error || "Context sync failed.")
  }

  function fetchContextRefreshEvents(after) {
    if (!contextRefresh?.paths?.events) return Promise.reject(new Error("Could not read context sync status."))

    const url = new URL(contextRefresh.paths.events, window.location.origin)
    url.searchParams.set("after", after)

    return fetch(url.toString(), {
      cache: "no-store",
      headers: noStoreJsonHeaders()
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not read context sync status.")
        return response.json()
      })
  }

  function waitForContextRefreshCompletion(after = 0) {
    return new Promise((resolve, reject) => {
      function poll(nextAfter) {
        fetchContextRefreshEvents(nextAfter)
          .then((payload) => {
            assertContextRefreshSucceeded(payload)
            if (contextRefreshFinished(payload)) {
              handleContextRefreshComplete(payload)
              resolve(payload)
              return
            }

            window.setTimeout(() => poll(payload.next_sequence ?? nextAfter), 1000)
          })
          .catch(reject)
      }

      poll(after)
    })
  }

  function startContextRefreshNow({ repositorySyncEventId = null } = {}) {
    if (contextRefreshing) {
      return contextRefresh?.paths?.events ? waitForContextRefreshCompletion(0) : Promise.resolve(null)
    }

    if (!contextRefresh?.paths?.start || contextGenerating) return Promise.resolve(null)

    setContextRefreshStreamLines([])
    setContextRefreshing(true)

    return fetch(contextRefresh.paths.start, {
      body: syncRequestBody(repositorySyncEventId),
      method: "POST",
      cache: "no-store",
      headers: syncMutationHeaders(repositorySyncEventId)
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not start context sync.")
        return response.json()
      })
      .then((payload) => {
        assertContextRefreshSucceeded(payload)
        if (contextRefreshFinished(payload)) {
          handleContextRefreshComplete(payload)
          return payload
        }

        return waitForContextRefreshCompletion(payload.next_sequence || 0)
      })
      .finally(() => {
        setContextRefreshing(false)
      })
  }

  function setSyncNowTaskStatus(key, status, detail = null) {
    setSyncNowTasks((current) => current.map((task) => (
      task.key === key ? { ...task, status, detail } : task
    )))
  }

  function failSyncNowTasks(failedKey, message) {
    setSyncNowTasks((current) => {
      const failedIndex = current.findIndex((task) => task.key === failedKey)
      return current.map((task, index) => {
        if (task.key === failedKey) return { ...task, status: "failed", detail: message }
        if (failedIndex >= 0 && index > failedIndex && task.status === "queued") {
          return { ...task, status: "skipped" }
        }
        return task
      })
    })
  }

  async function syncNow() {
    if (syncNowRunning) {
      setSyncProgressVisible(true)
      return
    }

    beginRepositoryConceptSwapBuffer()
    setSyncNowRunning(true)
    setSyncNowTasks(initialSyncNowTasks())
    setSyncNowEventId(null)
    setSyncProgressVisible(true)
    setSyncNowStatus("Fetching latest code")
    setSyncNowTaskStatus("fetching_code", "running")
    syncNowUpdatePhaseTaskKeyRef.current = "fetching_code"
    let repositorySyncEvent = null
    let currentSyncTaskKey = "fetching_code"

    try {
      repositorySyncEvent = await createRepositorySyncEvent()
      const repositorySyncEventId = repositorySyncEvent?.id || null
      setSyncNowEventId(repositorySyncEventId)
      conceptRefreshRepositorySyncEventIdRef.current = repositorySyncEventId
      const updatePayload = await startRepositoryUpdateNow({ repositorySyncEventId })
      SYNC_UPDATE_PHASE_TASK_KEYS.forEach((key) => setSyncNowTaskStatus(key, "completed"))
      currentSyncTaskKey = "syncing_context"
      setSyncNowStatus("Syncing context")
      setSyncNowTaskStatus("syncing_context", "running")
      await startContextRefreshNow({ repositorySyncEventId })
      setSyncNowTaskStatus("syncing_context", "completed")
      const issuesToCategorize = Array.isArray(updatePayload?.issues) ? updatePayload.issues : inboxIssues
      if (shouldStartStackIssueCategorization(issuesToCategorize, issueCategorizationTriggerPath)) {
        currentSyncTaskKey = "categorizing_issues"
        setSyncNowStatus("Assigning issue categories")
        setSyncNowTaskStatus("categorizing_issues", "running")
        await startIssueCategorization({ issuesToCategorize, waitForCompletion: true, repositorySyncEventId })
        setSyncNowTaskStatus("categorizing_issues", "completed")
      } else {
        setSyncNowTaskStatus("categorizing_issues", "skipped")
      }
      currentSyncTaskKey = "syncing_concepts"
      setSyncNowStatus("Syncing concepts")
      setSyncNowTaskStatus("syncing_concepts", "running")
      await waitForRepositoryConceptSwapCompletion({ repositorySyncEventId })
      setSyncNowTaskStatus("syncing_concepts", "completed")
      await updateRepositorySyncEvent(repositorySyncEvent, "completed")
      setSyncNowStatus("Sync complete")
    } catch (error) {
      const message = error.message || "Sync failed"
      try {
        await updateRepositorySyncEvent(repositorySyncEvent, "failed", message)
      } catch (auditError) {
        console.error(auditError)
      }
      failSyncNowTasks(
        currentSyncTaskKey === "fetching_code" ?
          syncNowUpdatePhaseTaskKeyRef.current || "fetching_code" :
          currentSyncTaskKey,
        message
      )
      setSyncNowStatus(message)
    } finally {
      if (repositoryConceptSwapActiveRef.current) discardRepositoryConceptSwapBuffer()
      setSyncNowRunning(false)
    }
  }

  function stopIssueCategorizationPolling() {
    if (!issueCategorizationPollRef.current) return

    window.clearInterval(issueCategorizationPollRef.current)
    issueCategorizationPollRef.current = null
  }

  function startIssueCategorizationPolling({ pollNow = false } = {}) {
    if (!issueCategorization?.paths?.status) {
      setIssueCategorizationStatus("failed")
      return
    }

    if (pollNow) pollIssueCategorizationStatus()
    if (issueCategorizationPollRef.current) return

    issueCategorizationPollRef.current = window.setInterval(pollIssueCategorizationStatus, 2000)
  }

  function fetchIssueCategorizationStatusPayload() {
    if (!issueCategorization?.paths?.status) {
      return Promise.reject(new Error("Could not check issue categories."))
    }

    return fetch(issueCategorization.paths.status, {
      cache: "no-store",
      headers: noStoreJsonHeaders()
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not check issue categories.")
        return response.json()
      })
  }

  function applyIssueCategorizationPayload(payload) {
    if (Array.isArray(payload.issues)) {
      setInboxIssues((current) => mergeInboxIssuePayloads(current, payload.issues))
    }

    if (payload.status === "running") {
      setIssueCategorizationStatus("running")
      return false
    }

    stopIssueCategorizationPolling()
    setIssueCategorizationStatus(payload.status || "idle")
    return true
  }

  function waitForIssueCategorizationCompletion() {
    if (!issueCategorization?.paths?.status) {
      return Promise.reject(new Error("Could not check issue categories."))
    }

    return new Promise((resolve, reject) => {
      function poll() {
        fetchIssueCategorizationStatusPayload()
          .then((payload) => {
            if (!applyIssueCategorizationPayload(payload)) {
              window.setTimeout(poll, 2000)
              return
            }

            assertIssueCategorizationSucceeded(payload)
            resolve(payload)
          })
          .catch(reject)
      }

      poll()
    })
  }

  function pollIssueCategorizationStatus() {
    fetchIssueCategorizationStatusPayload()
      .then(applyIssueCategorizationPayload)
      .catch((error) => {
        stopIssueCategorizationPolling()
        setIssueCategorizationStatus("failed")
        console.error(error)
      })
  }

  function startIssueCategorization({ issuesToCategorize = inboxIssues, waitForCompletion = false, repositorySyncEventId = null } = {}) {
    if (!issueCategorizationTriggerPath || issuesToCategorize.length === 0) {
      return Promise.resolve(null)
    }

    if (issueCategorizationStatus === "running") {
      return waitForCompletion ? waitForIssueCategorizationCompletion() : Promise.resolve(null)
    }

    stopIssueCategorizationPolling()
    setIssueCategorizationStatus("running")

    return fetch(issueCategorizationTriggerPath, {
      body: syncRequestBody(repositorySyncEventId),
      cache: "no-store",
      method: "POST",
      headers: syncMutationHeaders(repositorySyncEventId)
    })
      .then((response) => {
        if (!response.ok) {
          return response.json()
            .catch(() => ({}))
            .then((payload) => {
              throw new Error(payload.error || "Could not assign issue categories.")
            })
        }

        return response.json()
      })
      .then((payload) => {
        if (!applyIssueCategorizationPayload(payload)) {
          if (waitForCompletion) return waitForIssueCategorizationCompletion()
          startIssueCategorizationPolling()
        }
        assertIssueCategorizationSucceeded(payload)
        return payload
      })
      .catch((error) => {
        setIssueCategorizationStatus("failed")
        console.error(error)
        if (waitForCompletion) throw error
        return null
      })
  }

  function handleWorkspaceNavigate(section, event) {
    if (event && !shouldHandleClientNavigation(event)) return

    const nextSection = normalizeWorkspaceSection(section)
    const nextPath = workspaceSectionPaths[nextSection] || workspaceSectionHref(repository, nextSection, paths.root)

    event?.preventDefault()
    if (typeof window !== "undefined" && nextPath && window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath)
    }

    applyLocationState()
  }

  function handleWorkspaceBack() {
    if (inboxPanelOpen) {
      closeIssuePanel()
      return
    }

    if (activeWorkspaceSection === "domains" && contextDetailOpen) {
      navigateToContextPersonaList()
      return
    }

    if (activeWorkspaceSection === "concept" && conceptDetailView) {
      if (repositoryConcept?.selectedConceptView === "commits") {
        const selectedConceptKey = repositoryConcept.selectedConceptKey
        const selectedConcept = repositoryConcept?.concepts?.items?.find((item) => item?.key === selectedConceptKey)
        if (selectedConcept?.detailPath) {
          navigateToConcept(selectedConcept)
          return
        }
      }

      closeConceptDetail()
      return
    }

    setMobileWorkspaceNavOpen(true)
  }

  function navigateToInboxIssue(issue, event) {
    if (event && !shouldHandleClientNavigation(event)) return

    event?.preventDefault()
    if (typeof window !== "undefined" && issue.detailPath && window.location.pathname !== issue.detailPath) {
      window.history.pushState({}, "", issue.detailPath)
    }

    applyLocationState()
  }

  function closeIssuePanel() {
    const fallbackPath = workspaceSectionPaths[activeWorkspaceSection] || conceptListPath
    const returnPath = inboxPanelReturnPathRef.current || fallbackPath
    inboxPanelReturnPathRef.current = null
    if (typeof window !== "undefined" && returnPath && window.location.pathname !== returnPath) {
      window.history.pushState({}, "", returnPath)
    }

    applyLocationState()
  }

  function navigateToContextActionList(event) {
    if (event && !shouldHandleClientNavigation(event)) return

    event?.preventDefault()
    if (typeof window !== "undefined" && contextActionListPath && window.location.pathname !== contextActionListPath) {
      window.history.pushState({}, "", contextActionListPath)
    }

    applyLocationState()
  }

  function navigateToContextPersonaList(event) {
    if (event && !shouldHandleClientNavigation(event)) return

    event?.preventDefault()
    if (typeof window !== "undefined" && contextPersonaListPath && window.location.pathname !== contextPersonaListPath) {
      window.history.pushState({}, "", contextPersonaListPath)
    }

    applyLocationState()
  }

  function navigateToConceptList(event) {
    if (event && !shouldHandleClientNavigation(event)) return

    event?.preventDefault()
    if (typeof window !== "undefined" && conceptListPath && window.location.pathname !== conceptListPath) {
      window.history.pushState({}, "", conceptListPath)
    }

    applyLocationState()
  }

  function navigateToConcept(concept, event, { view = null } = {}) {
    if (event && !shouldHandleClientNavigation(event)) return

    const atlasType = concept?.atlasType || null
    if (atlasType === "persona" || atlasType === "action") {
      const atlasKey = contextKeyForAtlasNode(concept, atlasType)
      if (atlasKey) navigateToAtlasDetail(atlasType, atlasKey, event)
      return
    }

    const detailPath = view === "commits" ? conceptCommitHistoryPath(concept) : concept?.detailPath
    const conceptKey = concept?.key || null
    if (!detailPath || !conceptKey) return

    event?.preventDefault()
    if (typeof window !== "undefined" && window.location.pathname !== detailPath) {
      window.history.pushState({}, "", detailPath)
    }

    applyLocationState()
  }

  // Persona/action stars stay on the concepts page: the atlas sheet replaces
  // the concept detail in the same zoom presentation.
  function navigateToAtlasDetail(type, key, event) {
    event?.preventDefault()
    const nextPath = conceptAtlasDetailPath(conceptDetailBasePath, type, key)
    if (typeof window !== "undefined" && nextPath && window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath)
    }

    applyLocationState()
  }

  function navigateToContextAction(actionKey, event) {
    if (event && !shouldHandleClientNavigation(event)) return

    event?.preventDefault()
    const nextPath = contextActionDetailPath(contextActionListPath, actionKey)
    if (typeof window !== "undefined" && nextPath && window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath)
    }

    applyLocationState()
  }

  function handleContextActionKeyChange(actionKey) {
    if (actionKey) {
      navigateToContextAction(actionKey)
      return
    }

    navigateToContextActionList()
  }

  function navigateToContextPersona(personaKey, event) {
    if (event && !shouldHandleClientNavigation(event)) return

    event?.preventDefault()
    const nextPath = contextPersonaDetailPath(contextPersonaListPath, personaKey)
    if (typeof window !== "undefined" && nextPath && window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath)
    }

    applyLocationState()
  }

  function handleContextPersonaKeyChange(personaKey) {
    if (personaKey) {
      navigateToContextPersona(personaKey)
      return
    }

    navigateToContextPersonaList()
  }

  const workspaceTitle = workspaceSectionTitle(activeWorkspaceSection)
  const workspaceSubtitle = workspaceSectionSubtitle(activeWorkspaceSection, {
    contextGenerating: contextBusy
  })
  const syncStatus = syncNowStatus || (
    repositoryUpdateBusy(repositoryUpdate)
      ? "Code sync running"
      : contextRefreshing
        ? "Context sync running"
        : null
  )
  const syncMenu = readOnly ? null : {
    credentialSetupPath: agentGeneration?.credentialSetupPath,
    frequency: repositorySettings.syncCommitThreshold,
    frequencyOptions: [50, 100, 200],
    frequencySaving: syncSettingsSaving,
    onFrequencySelect: saveSyncCommitThreshold,
    onSyncNow: syncNow,
    syncBusy: syncNowRunning,
    syncDisabled: agentGeneration?.credentialAvailable === false || (!repositoryUpdate?.path && !repositoryUpdate?.statusPath && !contextRefresh?.paths?.start),
    syncDisabledReason: agentGeneration?.disabledReason,
    syncStatus
  }
  const syncNowSnapshot = useMemo(() => ({
    id: syncNowEventId ? `sync-${syncNowEventId}` : "sync-now",
    status: syncNowTasks.some((task) => task.status === "failed") ?
      "failed" :
      syncNowTasks.every((task) => ["completed", "failed", "skipped"].includes(task.status)) ? "completed" : "running",
    active: syncNowTasks.some((task) => !["completed", "failed", "skipped"].includes(task.status)),
    completedCount: syncNowTasks.filter((task) => task.status === "completed").length,
    runningCount: syncNowTasks.filter((task) => task.status === "running").length,
    skippedCount: syncNowTasks.filter((task) => task.status === "skipped").length,
    tasks: syncNowTasks,
    totalCount: syncNowTasks.length,
    statusPath: repositorySyncEventStatusPath(syncNowEventId),
    conceptsPath: repositoryConcept?.path || null
  }), [syncNowTasks, syncNowEventId, repositoryConcept?.path])

  return (
    <div className={`workspace-shell ${sidebarCollapsed ? "is-sidebar-collapsed" : ""} ${conceptDetailView ? "is-concept-zoom" : ""} ${atlasSectionActive ? "is-atlas-active" : ""} ${replayExit ? "is-replay" : ""}`.replace(/\s+/g, " ").trim()}>
      <WorkspaceSidebar
        replayExit={replayExit}
        activeSection={activeWorkspaceSection}
        collapsed={sidebarCollapsed}
        githubLogin={githubLogin || "GitHub"}
        mobileMenuOpen={mobileWorkspaceNavOpen}
        mobileVisible={mobileWorkspaceNavOpen || !conceptDetailView}
        onNavigate={handleWorkspaceNavigate}
        onSearch={openSearch}
        onOpenContext={() => handleWorkspaceNavigate("domains")}
        onToggleMobileMenu={() => setMobileWorkspaceNavOpen((current) => !current)}
        onToggleCollapsed={() => setSidebarCollapsed((current) => !current)}
        paths={paths}
        rail={(
          <div className="workspace-sidebar-sections">
            <IssueRail
              issues={inboxIssues}
              onHover={(issue) => setHoveredIssueKey(issue?.key || issue?.id || null)}
              onSelect={navigateToInboxIssue}
              sectionLabel="Inbox"
              selectedIssueKey={inboxPanelOpen ? inboxRoute.selectedIssueKey : null}
            />
            <ConceptRail
              concepts={repositoryConcept?.concepts?.items || []}
              onSelect={navigateToConcept}
              selectedConceptKey={repositoryConcept?.selectedConceptKey || null}
            />
          </div>
        )}
        repositories={repositorySwitcherRepositories}
        repository={repository}
        rootPath={paths.root}
        sectionPaths={workspaceSectionPaths}
        csrfToken={csrfToken}
        syncMenu={syncMenu}
      />

      <main className="workspace-main">
        <section className="content-stack page-scroll workspace-flow" id="repository-intake">
          <Notice alert={alert} />
          {mobileWorkspaceNavOpen ? (
            <MobileWorkspaceMenu
              conceptPath={conceptListPath}
              concepts={repositoryConcept?.concepts?.items || []}
              issues={inboxIssues}
              onConceptOverviewSelect={(event) => handleWorkspaceNavigate("concept", event)}
              onConceptSelect={navigateToConcept}
              onIssueSelect={navigateToInboxIssue}
              selectedConceptKey={repositoryConcept?.selectedConceptKey || null}
              selectedIssueKey={inboxPanelOpen ? inboxRoute.selectedIssueKey : null}
            />
          ) : null}

          {!mobileWorkspaceNavOpen && activeWorkspaceSection === "concept" ? (
            <WorkspaceSection
              hideTitle
              hideHero
              hideMobileHeader
              mobileActionInline
              mobileHeaderClassName="is-concept-header"
              mobileSubtitle={workspaceSubtitle}
              mobileTitle={null}
              onBack={handleWorkspaceBack}
              title="Concepts"
            />
          ) : null}

          {!mobileWorkspaceNavOpen && activeWorkspaceSection === "team" ? (
            <WorkspaceSection
              mobileSubtitle={workspaceSubtitle}
              mobileTitle={workspaceTitle}
              onBack={handleWorkspaceBack}
              title="Team"
            >
              <TeamPanel csrfToken={csrfToken} embedded team={team} />
            </WorkspaceSection>
          ) : null}

          {/* The persistent inbox engine: always mounted so its pollers and
              local issue state survive section switches. It floats the issue
              detail panel over whichever section is showing. */}
          <WorkspaceInboxContent
            readOnly={readOnly}
            canComment={inbox?.canComment === true}
            concept={repositoryConcept}
            contextActionListPath={contextActionListPath}
            contextOverview={repositoryContextOverview}
            contextPersonaListPath={contextPersonaListPath}
            contextStoryboards={contextStoryboards?.storyboards || []}
            inboxIssues={inboxIssues}
            mentionableMembers={team?.members || []}
            onIssueNavigate={navigateToInboxIssue}
            onIssueStatusToast={showInboxStatusToast}
            onIssueUpdate={handleIssueUpdate}
            onListNavigate={closeIssuePanel}
            selectedIssueKey={inboxRoute.selectedIssueKey}
          />

          {/* The persistent sky: mounted outside the section ternaries so
              switching workspace destinations parks the atlas instead of
              recreating it. Mobile CSS pairs the concept header with the sky
              via .workspace-mobile-header.is-concept-header ~ .concept-panel,
              so later siblings (the Context frame) are fine. */}
          <RepositoryConcepts
            onReplaySessionChange={handleReplaySessionChange}
            onReplayOpen={openReplay}
            onReplayClose={closeReplayRoute}
            onShareReplay={shareReplay}
            replayPath={paths.repositoryPlay}
            replayRequested={replayRequested}
            replayAutoplay={replayAutoplay}
            replayActive={Boolean(replayExit)}
            concept={repositoryConcept}
            contextActionListPath={contextActionListPath}
            contextDiagrams={contextDiagrams?.diagrams}
            contextOverview={repositoryContextOverview}
            contextPersonaListPath={contextPersonaListPath}
            contextStoryboards={contextStoryboards?.storyboards}
            issues={inboxIssues}
            onConceptChange={handleConceptChange}
            onConceptClose={closeConceptDetail}
            onConceptNavigate={navigateToConcept}
            onIssueClose={closeIssuePanel}
            onOpenContextAction={navigateToContextAction}
            onOpenContextPersona={navigateToContextPersona}
            onSummaryChange={handleConceptSummaryChange}
            sectionActive={atlasSectionActive}
            emphasizedIssueKey={inboxPanelOpen ? inboxRoute.selectedIssueKey : hoveredIssueKey}
            selectedIssueKey={inboxPanelOpen ? inboxRoute.selectedIssueKey : null}
            visualPaths={repositoryConcept?.visualPaths}
          />

          <ContextMarkdownEditor
            active={activeWorkspaceSection === "domains" && !contextDetailOpen && !mobileWorkspaceNavOpen}
            path={contextRefresh?.paths?.markdown}
            readOnly={readOnly}
            onBack={navigateToConceptList}
          />

          {contextDetailOpen && !mobileWorkspaceNavOpen ? (
            <WorkspaceSection title="Context" hideTitle onBack={navigateToConceptList}>
              <ContextOverview
                avatarRegenerationEnabled={Boolean(contextRefresh?.userAvatarRegenerationEnabled)}
                avatarRegenerationPath={contextRefresh?.paths?.regenerateUserAvatar}
                context={repositoryContextOverview}
                contextStatusPath={contextRefresh?.paths?.status}
                editable={!readOnly && !contextBusy}
                generating={contextGenerating || (contextRefreshing && !repositoryContextOverview)}
                initialDiagrams={contextDiagrams?.diagrams || []}
                initialSelectedPersonaKey={selectedContextPersonaKey}
                initialStoryboards={contextStoryboards?.storyboards || []}
                issues={inboxIssues}
                concept={repositoryConcept}
                onContextUpdate={handleRepositoryContextUpdate}
                onSelectedActionKeyChange={handleContextActionKeyChange}
                onSelectedPersonaKeyChange={handleContextPersonaKeyChange}
                selectedActionKey={selectedContextActionKey}
                selectedPersonaKey={selectedContextPersonaKey}
                diagramPaths={contextDiagrams?.paths}
                diagramRegenerationEnabled={Boolean(contextDiagrams?.regenerationEnabled)}
                storyboardPaths={contextStoryboards?.paths}
                streamLines={contextRefreshing ? contextRefreshStreamLines : architectureStreamLines}
                updatePath={contextRefresh?.paths?.update}
              />
            </WorkspaceSection>
          ) : null}

        </section>
      </main>

      {searchOpen ? <WorkspaceSearch items={searchableItems} onClose={() => setSearchOpen(false)} onSelect={selectSearchResult} /> : null}

      {inboxStatusToast ? (
        <WorkspaceToast key={inboxStatusToast.id} message={inboxStatusToast.message} />
      ) : null}

      {syncProgressVisible ? (
        <div className="repository-sync-progress-overlay">
          <RepositoryOnboardingProgress
            variant="sync"
            initialConceptItems={repositoryConcept?.concepts?.items || null}
            onDismiss={() => setSyncProgressVisible(false)}
            onboarding={syncNowSnapshot}
            repository={{
              displayName: repository?.displayName || repository?.fullName,
              fullName: repository?.fullName
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

function WorkspaceToast({ message }) {
  return (
    <div className="workspace-toast" role="status" aria-live="polite">
      {message}
    </div>
  )
}

export function WorkspaceInboxContent({
  readOnly = false,
  canComment = false,
  concept = null,
  contextActionListPath = null,
  contextOverview = null,
  contextPersonaListPath = null,
  contextStoryboards = [],
  inboxIssues = [],
  mentionableMembers = [],
  onIssueNavigate,
  onIssueStatusToast,
  onIssueUpdate,
  onListNavigate,
  selectedIssueKey
}) {
  if (inboxIssues.length === 0) return null

  return (
    <Inbox
      readOnly={readOnly}
      canComment={canComment}
      concept={concept}
      contextActionListPath={contextActionListPath}
      contextOverview={contextOverview}
      contextPersonaListPath={contextPersonaListPath}
      contextStoryboards={contextStoryboards}
      issues={inboxIssues}
      mentionableMembers={mentionableMembers}
      onIssueNavigate={onIssueNavigate}
      onIssueStatusToast={onIssueStatusToast}
      onIssueUpdate={onIssueUpdate}
      onListNavigate={onListNavigate}
      selectedIssueKey={selectedIssueKey}
    />
  )
}

export function VercelProjectPanel({ component, onUsageEnabled = null }) {
  const [projectState, setProjectState] = useState(() => component?.vercelProjects || null)
  const [usageSetup, setUsageSetup] = useState(null)
  const [usageSetupError, setUsageSetupError] = useState(null)
  const [usageSetupProjectId, setUsageSetupProjectId] = useState(null)

  useEffect(() => {
    if (!component?.vercelProjectsPath || component.vercelProjects) return

    let active = true
    setProjectState({ status: "loading" })

    fetch(component.vercelProjectsPath, {
      headers: { Accept: "application/json" }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load Vercel projects.")
        return response.json()
      })
      .then((payload) => {
        if (active) setProjectState(payload)
      })
      .catch((error) => {
        if (active) {
          setProjectState({
            status: "error",
            message: error.message || "Could not load Vercel projects."
          })
        }
      })

    return () => {
      active = false
    }
  }, [component?.id, component?.vercelProjectsPath])

  async function showUsageSetup(projectId) {
    if (!component?.vercelUsageSourcePath || usageSetupProjectId) return

    setUsageSetupError(null)
    setUsageSetupProjectId(projectId)
    try {
      const response = await fetch(component.vercelUsageSourcePath, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": readCsrfToken() || ""
        },
        body: JSON.stringify({ project_id: projectId })
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload?.message || "Could not prepare live usage.")
      setUsageSetup(payload)
      onUsageEnabled?.(vercelComponentWithLiveUsage(component, payload), payload.activityPath)
    } catch (error) {
      setUsageSetupError(error.message || "Could not prepare live usage.")
    } finally {
      setUsageSetupProjectId(null)
    }
  }

  if (!component?.vercelProjectsPath) return null

  const status = projectState?.status || "loading"
  const projects = Array.isArray(projectState?.projects) ? projectState.projects : []

  return (
    <section className="insight-panel slide-up vercel-project-panel" style={{ animationDelay: "0.08s" }}>
      <div className="focus-card vercel-project-card">
        <div className="vercel-project-header">
          <div>
            <div className="section-label">Vercel</div>
            <p className="stage-card-title">{status === "matched" ? "Vercel apps" : "Checking Vercel apps"}</p>
          </div>
          <span className={`vercel-status-pill is-${deploymentStatusTone(status)}`}>
            {vercelProjectStatusLabel(status, projects.length)}
          </span>
        </div>

        {status === "matched" && projects.length ? (
          <div className="vercel-project-list">
            {projects.map((project, index) => (
              <VercelProjectItem
                key={project.id || project.name || index}
                project={project}
                usageEnabled={component.vercelUsageEnabled && component.vercelUsageProjectId === project.id}
                usageLoading={usageSetupProjectId === project.id}
                usageSetup={usageSetup?.projectId === project.id ? usageSetup : null}
                onShowUsageSetup={component.vercelUsageSourcePath ? showUsageSetup : null}
              />
            ))}
          </div>
        ) : (
          <p className="vercel-project-message">
            {projectState?.message || "Looking for Vercel apps linked to this GitHub repository."}
          </p>
        )}
        {usageSetupError ? <p className="vercel-project-message is-error">{usageSetupError}</p> : null}
      </div>
    </section>
  )
}

export function vercelComponentWithLiveUsage(component, setup) {
  if (!component) return component

  return {
    ...component,
    vercelUsageEnabled: true,
    vercelUsageProjectId: setup?.projectId || component.vercelUsageProjectId
  }
}

export function repositoryConceptWithLiveUsage(concept, activityPath) {
  if (!concept || !activityPath) return concept

  return { ...concept, activityPath }
}

function VercelProjectItem({ project, usageEnabled = false, usageLoading = false, usageSetup = null, onShowUsageSetup = null }) {
  const deployment = project.latestDeployment

  return (
    <article className="vercel-project-item">
      <div className="vercel-project-item-header">
        <div>
          <p className="vercel-project-name">{project.name || "Unnamed app"}</p>
          <div className="vercel-project-meta">
            {project.repoUrl ? (
              <a href={project.repoUrl} target="_blank" rel="noreferrer">{project.repo}</a>
            ) : project.repo ? (
              <span>{project.repo}</span>
            ) : null}
            {project.productionBranch ? <span>{project.productionBranch}</span> : null}
            {project.rootDirectory ? <span>{project.rootDirectory}</span> : null}
            {project.framework ? <span>{project.framework}</span> : null}
          </div>
        </div>
        <span className={`vercel-status-pill is-${deploymentStatusTone(deployment?.readyState || deployment?.state)}`}>
          {deploymentStatusLabel(deployment)}
        </span>
      </div>

      {deployment ? (
        <div className="vercel-deployment-grid">
          <VercelDeploymentField label="Target" value={deployment.target || "preview"} />
          <VercelDeploymentField label="Created" value={formatVercelTimestamp(deployment.createdAt)} />
          <VercelDeploymentField label="Ready" value={formatVercelTimestamp(deployment.readyAt)} />
          <VercelDeploymentField label="Commit" value={deployment.commit?.shortSha || deployment.commit?.ref} />
        </div>
      ) : (
        <p className="vercel-project-message">No deployments returned for this app.</p>
      )}

      {deployment?.commit?.message ? (
        <p className="vercel-commit-message">{deployment.commit.message}</p>
      ) : null}

      {deployment?.errorMessage ? (
        <p className="vercel-project-message is-error">{deployment.errorMessage}</p>
      ) : null}

      <div className="vercel-project-actions">
        {deployment?.url ? (
          <a href={deployment.url} target="_blank" rel="noreferrer">Open deployment</a>
        ) : null}
        {deployment?.inspectorUrl ? (
          <a href={deployment.inspectorUrl} target="_blank" rel="noreferrer">Open inspector</a>
        ) : null}
        {onShowUsageSetup && project.id ? (
          <button type="button" disabled={usageLoading} onClick={() => onShowUsageSetup(project.id)}>
            {usageLoading ? "Preparing…" : usageEnabled ? "View live usage setup" : "Set up live usage"}
          </button>
        ) : null}
      </div>

      {usageSetup ? <VercelUsageSetup setup={usageSetup} /> : null}
    </article>
  )
}

function VercelUsageSetup({ setup }) {
  return (
    <div className="vercel-usage-setup">
      <p className="vercel-usage-setup-title">Add a production Log Drain in Vercel</p>
      <p className="vercel-project-message">Use JSON, production, and Logs. Conceptive keeps minute totals—not raw log messages.</p>
      <VercelUsageSetupValue label="Endpoint" value={setup.endpointUrl} />
      <VercelUsageSetupValue label="Signature secret" value={setup.signatureSecret} />
    </div>
  )
}

function VercelUsageSetupValue({ label, value }) {
  const [copied, setCopied] = useState(false)

  async function copyValue() {
    if (!value || !navigator.clipboard?.writeText) return
    await navigator.clipboard.writeText(value)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1_600)
  }

  return (
    <div className="vercel-usage-setup-value">
      <span>{label}</span>
      <code>{value}</code>
      <button type="button" onClick={copyValue} aria-label={`Copy ${label.toLowerCase()}`}>
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  )
}

function VercelDeploymentField({ label, value }) {
  if (!value) return null

  return (
    <div className="vercel-deployment-field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function vercelProjectStatusLabel(status, projectCount) {
  if (status === "matched") return `${projectCount} ${projectCount === 1 ? "app" : "apps"}`
  return deploymentStatusLabel(null, status)
}

function deploymentStatusLabel(deployment, fallbackStatus) {
  const status = deployment?.readyState || deployment?.state || fallbackStatus
  if (status === "loading") return "Loading"
  if (status === "matched" && !deployment) return "Matched"
  if (!status) return "Unknown"

  return status.toString().replaceAll("_", " ").toLowerCase()
}

function deploymentStatusTone(status) {
  const normalized = status?.toString().toLowerCase()
  if ([ "ready", "matched", "promoted" ].includes(normalized)) return "ready"
  if ([ "error", "failed" ].includes(normalized)) return "error"
  if ([ "canceled", "cancelled" ].includes(normalized)) return "canceled"
  if ([ "not_found", "unavailable" ].includes(normalized)) return "muted"

  return "pending"
}

function formatVercelTimestamp(value) {
  if (!value) return null

  const numeric = Number(value)
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 1_000_000_000_000 ? numeric * 1000 : numeric)
    : new Date(value)

  if (Number.isNaN(date.getTime())) return null

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date)
}

// Concept selection is owned by navigation (card clicks, back, popstate) —
// never by async payload echoes. Fetches capture the concept prop when they
// start, so a poll begun on the detail page resolves carrying the old
// selectedConceptKey; merging it naively would undo a back-navigation that
// happened while the request was in flight.
export function mergeConceptPayload(current, incoming) {
  return mergeRepositoryConceptPayload(current, incoming)
}

function WorkspaceSection({
  action,
  children,
  compactMobileHeader = false,
  desktopBackLabel = "Back",
  eyebrow,
  hideHero = false,
  hideMobileHeader = false,
  hideTitle = false,
  heroClassName = "",
  mobileAction,
  mobileActionInline = false,
  mobileHeaderClassName = "",
  mobileSubtitle,
  mobileTitle,
  onBack,
  showDesktopBack = false,
  subtitle,
  title
}) {
  const renderedMobileAction = mobileAction === undefined ? action : mobileAction
  const hasMobileTopbar = Boolean(mobileTitle || (mobileActionInline && renderedMobileAction))
  const hasHeroText = Boolean(eyebrow || (!hideTitle && title) || subtitle)
  const renderHero = !hideHero && Boolean(hasHeroText || action)

  return (
    <>
      {showDesktopBack ? (
        <div className="workspace-desktop-back-row slide-up">
          <BackButton onClick={onBack} iconOnly label={desktopBackLabel} />
        </div>
      ) : null}

      {hideMobileHeader ? null : (
        <div className={["workspace-mobile-header", "slide-up", mobileHeaderClassName, compactMobileHeader ? "is-compact" : null].filter(Boolean).join(" ")}>
          <div className="workspace-mobile-back-row">
            <BackButton onClick={onBack} iconOnly label="Back" />
          </div>
          {hasMobileTopbar ? (
            <div className={`workspace-mobile-topbar ${mobileActionInline ? "has-inline-action" : ""}`.trim()}>
              {mobileTitle ? (
                <div className="workspace-mobile-title-row">
                  <h1 className="workspace-mobile-title">{mobileTitle}</h1>
                </div>
              ) : null}
              {mobileActionInline && renderedMobileAction ? <div className="workspace-mobile-action is-inline">{renderedMobileAction}</div> : null}
            </div>
          ) : null}
          {mobileSubtitle ? <p className="workspace-mobile-subtitle">{mobileSubtitle}</p> : null}
          {!mobileActionInline && renderedMobileAction ? <div className="workspace-mobile-action">{renderedMobileAction}</div> : null}
        </div>
      )}

      {renderHero ? (
        <div className={`system-hero slide-up ${heroClassName}`.trim()}>
          {hasHeroText ? (
            <div className="system-hero-main">
              {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
              {!hideTitle && title ? <h1 className="page-title">{title}</h1> : null}
              {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
            </div>
          ) : null}
          {action ? <div className="system-hero-action">{action}</div> : null}
        </div>
      ) : null}

      {children}
    </>
  )
}

export function MobileWorkspaceMenu({ conceptPath, concepts = [], issues = [], onConceptOverviewSelect, onConceptSelect, onIssueSelect, selectedConceptKey = null, selectedIssueKey = null }) {
  return (
    <div className="workspace-mobile-menu">
      <div className="workspace-mobile-menu-sections">
        <IssueRail
          issues={issues}
          onSelect={onIssueSelect}
          sectionLabel="Inbox"
          selectedIssueKey={selectedIssueKey}
          variant="mobile"
        />
        <section className="workspace-mobile-menu-concepts">
          <h2 className="rail-section-label">
            <a
              className="workspace-mobile-menu-overview"
              href={conceptPath || "#"}
              onClick={onConceptOverviewSelect}
            >
              Concepts
            </a>
          </h2>
          <ConceptRail
            concepts={concepts}
            onSelect={onConceptSelect}
            selectedConceptKey={selectedConceptKey}
            showSectionLabel={false}
          />
        </section>
      </div>
    </div>
  )
}

function workspaceSectionTitle(activeWorkspaceSection) {
  if (activeWorkspaceSection === "domains") return "Context"
  if (activeWorkspaceSection === "concept") return "Concepts"
  if (activeWorkspaceSection === "team") return "Team"

  return null
}

function workspaceSectionSubtitle(activeWorkspaceSection, { contextGenerating }) {
  if (activeWorkspaceSection === "domains") {
    return contextGenerating
      ? "Your product context is being generated."
      : null
  }

  return null
}

function repositoryConceptActive(concept) {
  const summary = concept?.concepts?.summary || concept?.summary
  const items = concept?.concepts?.items || []
  const replayTranscript = concept?.concepts?.replayTranscript || null
  const contextTimeline = concept?.contextTimeline || null
  const contextConstellations = concept?.contextConstellations || null

  return conceptAsyncActive(summary, items, contextTimeline, contextConstellations, replayTranscript) || conceptReadingPaused(summary)
}

export function repositoryConceptReadyForAtomicSwap(concept) {
  return !repositoryConceptActive(concept)
}

function repositoryConceptPayloadFromConceptsResponse(payload, currentConcept = null, conceptPath = null) {
  const nextConcepts = payload?.concepts?.items ? payload.concepts : payload?.concept?.concepts
  if (!nextConcepts?.items) return null

  const current = currentConcept || {}

  return {
    ...current,
    path: conceptPath || current.path,
    concepts: nextConcepts,
    contextTimeline: payload?.contextTimeline || payload?.concept?.contextTimeline || current.contextTimeline || null,
    contextConstellations: payload?.contextConstellations || payload?.concept?.contextConstellations || current.contextConstellations || null,
    summary: nextConcepts.summary || current.summary || null
  }
}

function repositoryConceptSnapshotForSyncEvent(concept) {
  if (!concept?.concepts?.items) return null

  return concept
}

export function mergeRepositoryConceptPayload(current, nextConcept) {
  if (!nextConcept) return current
  if (!current) return nextConcept

  const currentHasSelectedConceptKey = Object.prototype.hasOwnProperty.call(current, "selectedConceptKey")
  const currentHasSelectedConceptView = Object.prototype.hasOwnProperty.call(current, "selectedConceptView")
  const currentHasSelectedAtlas = Object.prototype.hasOwnProperty.call(current, "selectedAtlas")

  return {
    ...current,
    ...nextConcept,
    selectedAtlas: currentHasSelectedAtlas ? current.selectedAtlas || null : nextConcept.selectedAtlas || null,
    selectedConceptKey: currentHasSelectedConceptKey ? current.selectedConceptKey || null : nextConcept.selectedConceptKey || null,
    selectedConceptView: currentHasSelectedConceptView ? current.selectedConceptView || null : nextConcept.selectedConceptView || null
  }
}

const stackComponentCategoryAreas = {
  "AI Coding": "codegen",
  "Repository User Interface": "frontend",
  "Repository App Logic": "backend",
  "Repository Auth & Security": "security",
  Hosting: "codehost",
  Database: "database",
  Services: "services",
  Monitor: "observability"
}

function stackComponentAreaId(category) {
  return stackComponentCategoryAreas[category] || null
}

export function shouldStartStackIssueCategorization(issues = [], triggerPath) {
  return Boolean(triggerPath && hasUncategorizedStackIssues(issues))
}

function hasUncategorizedStackIssues(issues = []) {
  return issues.length > 0 && issues.some((issue) => !stackComponentAreaId(issue.stackComponentCategory))
}

function readInitialWorkspaceSection(initialSection, options = {}) {
  const normalizedInitialSection = normalizeWorkspaceSection(initialSection, null)
  if (normalizedInitialSection) return normalizedInitialSection
  if (typeof window === "undefined") return "concept"

  return readWorkspaceSectionFromLocation(options)
}

function readInitialInboxRoute(inbox = null) {
  const selectedIssueKey = inbox?.selectedIssueKey || null

  return {
    view: selectedIssueKey ? "detail" : null,
    selectedIssueKey
  }
}

function readInitialContextActionKey(contextActions = null) {
  return contextActions?.selectedActionKey || contextActionKeyFromPathname(windowPathname())
}

function readInitialContextPersonaKey(contextPersonas = null) {
  return contextPersonas?.selectedPersonaKey || contextPersonaKeyFromPathname(windowPathname())
}

function windowPathname() {
  return typeof window === "undefined" ? null : window.location?.pathname || null
}

// The URL is the canonical navigation state. These pure readers derive the
// section, issue panel, and concept/atlas/context selections from a pathname,
// so stale server props or leftover local state can never resurrect a closed
// panel or stack the two zoom panels.
function repositoryRouteSegments(pathname) {
  const pathSegments = String(pathname || "").split("/").filter(Boolean)
  const repositoryIndex = pathSegments[0] === "workspaces" && pathSegments[2] === "repositories" ? 2 : -1
  const sectionIndex = repositoryIndex >= 0 ? repositoryIndex + 3 : 1

  return { pathSegments, repositoryIndex, sectionIndex }
}

function inboxRouteFromPathname(pathname) {
  const { pathSegments, sectionIndex } = repositoryRouteSegments(pathname)
  const routeSection = pathSegments[sectionIndex]
  const selectedIssueKey = routeSection === "inbox" ? safeDecodeURIComponent(pathSegments[sectionIndex + 1]) : null

  return {
    view: selectedIssueKey ? "detail" : null,
    selectedIssueKey: selectedIssueKey || null
  }
}

function contextActionKeyFromPathname(pathname) {
  const { pathSegments, sectionIndex } = repositoryRouteSegments(pathname)
  const routeSection = pathSegments[sectionIndex]
  const routeResource = pathSegments[sectionIndex + 1]
  const actionKey = pathSegments[sectionIndex + 2]

  return routeSection === "context" && routeResource === "actions" ? safeDecodeURIComponent(actionKey) : null
}

function contextPersonaKeyFromPathname(pathname) {
  const { pathSegments, sectionIndex } = repositoryRouteSegments(pathname)
  const routeSection = pathSegments[sectionIndex]
  const routeResource = pathSegments[sectionIndex + 1]
  const personaKey = pathSegments[sectionIndex + 2]

  return routeSection === "context" && routeResource === "personas" ? safeDecodeURIComponent(personaKey) : null
}

function conceptKeyFromPathname(pathname) {
  const { pathSegments, sectionIndex } = repositoryRouteSegments(pathname)
  const routeSection = pathSegments[sectionIndex]
  const conceptKey = pathSegments[sectionIndex + 1]
  const nestedKey = pathSegments[sectionIndex + 2]

  if (routeSection !== "concept") return null
  if (conceptKey === "commits" && nestedKey) return safeDecodeURIComponent(nestedKey)
  // `concept/personas/:key` and `concept/actions/:key` select an atlas sheet,
  // not a concept.
  if ((conceptKey === "personas" || conceptKey === "actions") && nestedKey) return null

  return safeDecodeURIComponent(conceptKey)
}

export function conceptViewFromPathname(pathname) {
  const { pathSegments, sectionIndex } = repositoryRouteSegments(pathname)
  if (pathSegments[sectionIndex] !== "concept") return null
  return pathSegments[sectionIndex + 1] === "commits" && pathSegments[sectionIndex + 2] ? "commits" : null
}

function atlasSelectionFromPathname(pathname) {
  const { pathSegments, sectionIndex } = repositoryRouteSegments(pathname)
  const routeSection = pathSegments[sectionIndex]
  const routeResource = pathSegments[sectionIndex + 1]
  const routeKey = pathSegments[sectionIndex + 2]

  if (routeSection !== "concept" || !routeKey) return null
  if (routeResource === "personas") return { type: "persona", key: safeDecodeURIComponent(routeKey) }
  if (routeResource === "actions") return { type: "action", key: safeDecodeURIComponent(routeKey) }

  return null
}

// Canonical navigation derivation: the pathname alone decides which section,
// issue panel, and selections are active. Issue detail URLs float the panel
// over whichever section is showing, so they keep the current section and
// clear every other selection — the issue and concept zoom panels can never
// stack.
export function navigationStateFromLocation(pathname, { currentSection = null, repository = null, replayActive = false } = {}) {
  const { pathSegments, sectionIndex } = repositoryRouteSegments(pathname)
  const routeSection = pathSegments[sectionIndex]

  if (routeSection === "inbox") {
    return {
      section: currentSection || "concept",
      replayRequested: false,
      inboxRoute: inboxRouteFromPathname(pathname),
      selectedConceptKey: null,
      selectedConceptView: null,
      selectedAtlas: null,
      contextActionKey: null,
      contextPersonaKey: null
    }
  }

  const section = workspaceSectionFromPathname(pathname, { repository })
  const selectedConceptKey = section === "concept" ? conceptKeyFromPathname(pathname) : null
  const selectedAtlas = section === "concept" ? atlasSelectionFromPathname(pathname) : null

  return {
    section,
    replayRequested: routeSection === "play" || (replayActive && Boolean(selectedConceptKey || selectedAtlas)),
    inboxRoute: { view: null, selectedIssueKey: null },
    selectedConceptKey,
    selectedConceptView: section === "concept" ? conceptViewFromPathname(pathname) : null,
    selectedAtlas,
    contextActionKey: section === "domains" ? contextActionKeyFromPathname(pathname) : null,
    contextPersonaKey: section === "domains" ? contextPersonaKeyFromPathname(pathname) : null
  }
}

function conceptAtlasDetailPath(listPath, type, key) {
  const resource = type === "persona" ? "personas" : type === "action" ? "actions" : null
  if (!listPath || !resource || !key) return null

  return `${listPath.replace(/\/$/, "")}/${resource}/${encodeURIComponent(key)}`
}

function appendQueryParam(path, key, value) {
  if (!path) return path

  const separator = path.includes("?") ? "&" : "?"
  return `${path}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`
}

function contextActionDetailPath(listPath, actionKey) {
  if (!listPath || !actionKey) return null

  return `${listPath.replace(/\/$/, "")}/actions/${encodeURIComponent(actionKey)}`
}

function contextPersonaDetailPath(listPath, personaKey) {
  if (!listPath || !personaKey) return null

  return `${listPath.replace(/\/$/, "")}/personas/${encodeURIComponent(personaKey)}`
}

function contextKeyForAtlasNode(node = {}, type) {
  if (node.contextRouteKey) return node.contextRouteKey
  if (node.contextKey) return node.contextKey

  const prefix = `${type}:`
  return typeof node.key === "string" && node.key.startsWith(prefix) ? node.key.slice(prefix.length) : null
}

function readWorkspaceSectionFromLocation(options = {}) {
  if (typeof window === "undefined") return "concept"

  const hashSection = normalizeWorkspaceSection(window.location?.hash?.toString(), null)
  if (hashSection) return hashSection

  return workspaceSectionFromPathname(window.location?.pathname, options)
}

function workspaceSectionFromPathname(pathname, options = {}) {
  const { pathSegments, sectionIndex } = repositoryRouteSegments(pathname)
  const routeSection = pathSegments[sectionIndex]
  if (!routeSection && repositoryLandingPathMatches(options.repository, pathname)) return "concept"

  return normalizeWorkspaceSection(routeSection, "concept")
}

function repositoryLandingPathMatches(repository, pathname) {
  if (!repository?.fullName) return false

  const { pathSegments, repositoryIndex } = repositoryRouteSegments(pathname)
  if (repository?.publicSlug && pathSegments.length === 1) {
    return safeDecodeURIComponent(pathSegments[0])?.toLowerCase() === repository.publicSlug.toLowerCase()
  }
  if (repositoryIndex < 0) return false

  const owner = safeDecodeURIComponent(pathSegments[repositoryIndex + 1])
  const repositoryName = safeDecodeURIComponent(pathSegments[repositoryIndex + 2])
  const routeSection = pathSegments[repositoryIndex + 3]
  if (!owner || !repositoryName || routeSection) return false

  return `${owner}/${repositoryName}`.toLowerCase() === repository.fullName.toLowerCase()
}

function safeDecodeURIComponent(value) {
  if (!value) return null

  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
